import { useState } from 'react';
import { Modal } from './Modal';

/**
 * Nombre corto de una persona: los dos primeros nombres y el primer apellido
 * ("Jhon Alexander Tuquerres Jimenez" -> "Jhon Alexander Tuquerres"). Heuristica: maximo 3 palabras;
 * si el nombre ya tiene 3 o menos palabras se muestra completo.
 */
export function shortName(full: string | null | undefined): string {
  const words = (full ?? '').trim().split(/\s+/).filter(Boolean);
  return words.slice(0, 3).join(' ');
}

/**
 * Codigo corto para columnas fijas en pantallas angostas: 4 letras del nombre + "-" + 2 letras del primer apellido.
 * Heuristica: 1 palabra -> solo las 4 letras; 2 palabras -> la segunda es el apellido; 3 palabras -> la segunda
 * (segundo nombre o primer apellido, no se distingue); 4 o mas -> la tercera (dos nombres + primer apellido).
 * "JHON ANDRES GUISAO VALENCIA" -> "JHON-GU";  "JHON JIMENEZ" -> "JHON-JI".
 */
export function nameCode(full: string | null | undefined): string {
  const words = (full ?? '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';
  const first = words[0].slice(0, 4);
  const second = words.length === 1 ? '' : words.length <= 3 ? words[1] : words[2];
  return (second ? `${first}-${second.slice(0, 2)}` : first).toUpperCase();
}

/** Celda de nombre para tablas: nombre corto en una sola linea y "ver" para abrir el nombre completo. */
export function NameCell({ name, compact = false }: { name: string | null | undefined; compact?: boolean }) {
  const [open, setOpen] = useState(false);
  const full = (name ?? '').trim();
  if (!full) return <span className="text-ink-300">—</span>;
  if (compact) {
    return (
      <>
        <button type="button" onClick={(e) => { e.stopPropagation(); setOpen(true); }} title="Ver nombre completo" className="whitespace-nowrap font-mono text-xs font-bold tracking-wide text-brand-700 underline decoration-dotted underline-offset-4">
          {nameCode(full)}
        </button>
        <Modal open={open} onClose={() => setOpen(false)} title="Nombre completo">
          <p className="break-words text-base font-semibold text-ink-900">{full}</p>
          <button type="button" onClick={() => setOpen(false)} className="mt-4 w-full rounded-xl bg-ink-100 px-4 py-3 text-sm font-semibold text-ink-700">Cerrar</button>
        </Modal>
      </>
    );
  }
  const short = shortName(full);
  if (short === full) return <span className="whitespace-nowrap">{full}</span>;
  return (
    <span className="whitespace-nowrap">
      {open ? full : `${short}…`}{' '}
      <button type="button" onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }} className="text-xs font-semibold text-brand-600 hover:underline">
        {open ? 'menos' : 'ver'}
      </button>
    </span>
  );
}
